import { ChangeEvent, useMemo, useState } from 'react';
import { Box, Modal, Text, Icon, IconButton } from '@nimbus-ds/components';
import { CheckCircleIcon, MoneyIcon, TrashIcon } from '@nimbus-ds/icons';
import { CancelAndConfirmButtons, InputNumberNimbus } from 'commons/components';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { OrderEditDiscount } from '../../Products';

interface DiscountEditModalProps {
  show: boolean;
  closeModal: () => void;
  discounts: OrderEditDiscount[];
  saveDiscount: (newDiscount: { value: number }) => void;
  removeDiscount: (discountType: string) => void;
  maxDiscount: number;
}

function DiscountEditModal({
  show,
  closeModal,
  discounts,
  saveDiscount,
  removeDiscount,
  maxDiscount,
}: Readonly<DiscountEditModalProps>) {
  const t = useTranslationOrders();

  const initialDiscountValue = useMemo(
    () => discounts.find((d) => d.discountType === 'coupon_discount')?.value,
    [discounts],
  );

  const [discount, setDiscount] = useState<string>(
    initialDiscountValue?.toString() ?? '',
  );
  const [hasError, setHasError] = useState(false);
  const discountNumber = discount !== '' ? Number(discount) : undefined;

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newDiscountValue = e.target.value;
    const newDiscountValueNumber =
      newDiscountValue !== '' ? Number(newDiscountValue) : undefined;
    setHasError(
      newDiscountValueNumber !== undefined &&
        (newDiscountValueNumber === 0 ||
          Number(newDiscountValueNumber) > maxDiscount),
    );
    setDiscount(newDiscountValue);
  };

  const handleConfirm = () => {
    const discountValueNumber = discount !== '' ? Number(discount) : undefined;
    saveDiscount({
      value: discountValueNumber ?? 0,
    });
    closeModal();
  };

  const handleRemove = () => {
    removeDiscount('coupon_discount');
    setDiscount('');
    setHasError(false);
    closeModal();
  };

  const handleCloseModal = () => {
    setDiscount(initialDiscountValue?.toString() ?? '');
    setHasError(false);
    closeModal();
  };

  const editDiscount = discounts.find(
    (discount) => discount.discountType === 'coupon_discount',
  );
  const isConfirmDisabled = hasError || discount === '';

  return (
    <Modal open={show} onDismiss={handleCloseModal}>
      <Modal.Header
        title={
          editDiscount
            ? t('editOrders.discountModal.titleEdit')
            : t('editOrders.discountModal.titleAdd')
        }
      />
      <Modal.Body padding="none">
        <Box display="flex" flexDirection="column" gap="4">
          <Text color="neutral-textLow">
            {t('editOrders.discountModal.description')}
          </Text>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box paddingTop="2">
              <Text color="neutral-textHigh">
                {t('editOrders.discountModal.customDiscount')}
              </Text>
            </Box>
            <Box
              display="flex"
              gap="1"
              alignItems="flex-start"
              maxWidth="160px"
            >
              <Box>
                <InputNumberNimbus
                  appearance={hasError ? 'danger' : 'neutral'}
                  type="float2d"
                  onChange={handleInputChange}
                  append={<Icon source={<MoneyIcon color="grey" />} />}
                  defaultDecimals={2}
                  value={discount}
                />
                {hasError && (
                  <Box display="flex" gap="0-5" paddingTop="1">
                    <Icon
                      color="danger-textLow"
                      source={<CheckCircleIcon size="small" />}
                    />
                    <Text
                      as="span"
                      color="danger-textLow"
                      fontSize="caption"
                      lineHeight="caption"
                    >
                      {discountNumber && discountNumber > maxDiscount
                        ? t('editOrders.discountModal.errorMaxCaption')
                        : t('editOrders.discountModal.errorCaption')}
                    </Text>
                  </Box>
                )}
              </Box>
              {editDiscount && (
                <Box width="32px">
                  <IconButton
                    source={<TrashIcon />}
                    onClick={handleRemove}
                    size="2rem"
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <CancelAndConfirmButtons
          onCancel={handleCloseModal}
          onConfirm={handleConfirm}
          isConfirmDisabled={isConfirmDisabled}
          confirmText={
            !editDiscount ? t('editOrders.discountModal.addButton') : null
          }
        />
      </Modal.Footer>
    </Modal>
  );
}

export default DiscountEditModal;
